export interface CountrySearchSource {
  code: string
  name: string
  englishName?: string
  nativeNames?: readonly string[]
  searchSynonyms?: readonly string[]
}

/** Search spelling shared by country names, admin synonyms and the typed query. */
export function normalizeCountrySearch(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .replace(/ß/g, 'ss')
    .replace(/ł/g, 'l')
    .replace(/ø/g, 'o')
    .replace(/æ/g, 'ae')
    .replace(/œ/g, 'oe')
    .replace(/[đð]/g, 'd')
    .replace(/þ/g, 'th')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
}

/** Every typed word must prefix a word in one of the country's searchable names. */
export function matchesCountrySearch(country: CountrySearchSource, query: string): boolean {
  const normalized = normalizeCountrySearch(query)
  if (!normalized)
    return true

  const words = [
    country.code,
    country.name,
    country.englishName ?? '',
    ...(country.nativeNames ?? []),
    ...(country.searchSynonyms ?? []),
  ].flatMap(value => normalizeCountrySearch(value).split(' ').filter(Boolean))

  return normalized.split(' ').every(part => words.some(word => word.startsWith(part)))
}

/** The visible name always follows the current site's language. */
export function countryDisplayName(code: string, language: string): string {
  try {
    return new Intl.DisplayNames([language], { type: 'region' }).of(code.toUpperCase()) ?? code.toUpperCase()
  }
  catch {
    return code.toUpperCase()
  }
}

export function countryCurrencySymbol(currency: string, language: string): string {
  try {
    return new Intl.NumberFormat(language, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0).find(part => part.type === 'currency')?.value ?? currency
  }
  catch {
    return currency
  }
}

/**
 * CLDR's likely local language supplies native country names for newly added
 * countries automatically. Include the store language for multilingual markets;
 * admin synonyms can add further local spellings without a storefront release.
 */
export function countryNativeNames(code: string, storeLanguage?: string): string[] {
  const languages = new Set<string>()
  try {
    languages.add(new Intl.Locale(`und-${code.toUpperCase()}`).maximize().language)
  }
  catch {
    // Invalid metadata should leave the country searchable by its other names.
  }
  if (storeLanguage) {
    try {
      languages.add(new Intl.Locale(storeLanguage).language)
    }
    catch {
      // Store URLs can use market codes that are not valid language subtags.
    }
  }
  return [...new Set([...languages].map(language => countryDisplayName(code, language)))]
}

/** The `storefront-country` cookie: the shopper's destination and the store serving it. */
export interface StorefrontCountryPreference {
  code: string
  path: string
}

/** A storefront country row, as far as market membership is concerned. */
export interface StorefrontMarketCountry {
  code: string
  market: { path: string }
}

/** The cookie as written by the selector, or `null` for anything else a browser may hold. */
export function parseStorefrontCountryPreference(value: unknown): StorefrontCountryPreference | null {
  if (!value || typeof value !== 'object')
    return null
  const { code, path } = value as Partial<Record<keyof StorefrontCountryPreference, unknown>>
  if (typeof code !== 'string' || !/^[A-Z]{2}$/.test(code) || typeof path !== 'string')
    return null
  return { code, path }
}

/**
 * The pathname of a market URL on this origin, without a trailing slash, or
 * `undefined` for another domain. A market URL is either a path (`/de`) or an
 * absolute store URL, so both resolve against the current origin.
 */
export function sameOriginMarketPath(path: string, origin: string): string | undefined {
  try {
    const target = new URL(path, origin)
    if (target.origin === origin)
      return target.pathname.replace(/\/+$/, '')
  }
  catch {
    // An outdated or malformed market URL must not break the caller.
  }
}

/** Whether a market URL is the storefront for `locale` on this origin. */
export function isCurrentMarketPath(path: string, market: { origin: string, locale: string }): boolean {
  return sameOriginMarketPath(path, market.origin) === `/${market.locale}`
}

/**
 * The saved destination country, but only where it belongs to the market the
 * shopper is on: a preference made for another storefront (or another domain)
 * says nothing about this one, and a country this market no longer serves must
 * not be quoted, seeded into an order or ticked.
 *
 * The market's own default country needs no list: it is what every caller
 * falls back to anyway. Any other code is confirmed against the backend's
 * country list, and `null` while that list is unknown, so callers fall back to
 * the locale's country rather than guess.
 */
export function currentMarketCountryCode(
  value: unknown,
  market: { origin: string, locale: string, defaultCountry: string, countries?: readonly StorefrontMarketCountry[] | null },
): string | null {
  const preference = parseStorefrontCountryPreference(value)
  if (!preference || !isCurrentMarketPath(preference.path, market))
    return null
  if (preference.code === market.defaultCountry.toUpperCase())
    return preference.code
  const country = market.countries?.find(item => item.code.toUpperCase() === preference.code)
  return country && isCurrentMarketPath(country.market.path, market) ? preference.code : null
}

/**
 * Where picking a country takes the shopper:
 * - `current`: the market they are on serves it, so nothing needs to load;
 * - `locale`: another storefront on this origin (`/cs`), named by its prefix;
 * - `external`: another domain, or a market URL that is not a locale root.
 */
export type MarketDestination
  = | { kind: 'current' }
    | { kind: 'locale', locale: string }
    | { kind: 'external' }

export function marketDestination(path: string, market: { origin: string, locale: string }): MarketDestination {
  const targetPath = sameOriginMarketPath(path, market.origin)
  const targetLocale = targetPath === undefined ? undefined : /^\/([^/]+)$/.exec(targetPath)?.[1]
  if (!targetLocale)
    return { kind: 'external' }
  return targetLocale === market.locale ? { kind: 'current' } : { kind: 'locale', locale: targetLocale }
}

/** Whether `path` (as `switchLocalePath` returns it) is a page of `locale`'s storefront. */
export function isLocalePagePath(path: string, locale: string): boolean {
  const prefix = `/${locale}`
  return path.startsWith(prefix) && (path.length === prefix.length || '/?#'.includes(path.charAt(prefix.length)))
}
